import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { deleteRolByIdAsync, getRolListAsync } from "../../../api/Rols";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  PAGINACION_ARIA,
  encabezadoTabla,
  normalizar,
  useCargaLocal,
  useConfirmacion,
  useEscritura,
} from "../comunes";

const FILAS_POR_PAGINA = 10;

const RolList = () => {
  const [rolList, setRolList] = useState([]);
  const [localReload, setLocalReload] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const { cargando, error, cargar } = useCargaLocal(() => getRolListAsync(token));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setRolList(result.data.result || []);
    })();
    return () => {
      vigente = false;
    };
  }, [localReload]);

  const nombreDe = (idRol) => rolList.find((r) => r.id === idRol)?.rolName;

  const termino = normalizar(searchTerm.trim());
  const withSearch = rolList.filter((val) => {
    if (termino === "") return true;
    return normalizar(val.id).includes(termino) || normalizar(val.rolName).includes(termino);
  });

  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) setPagina(0);
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  const borrar = async (fila) => {
    // El servidor rechaza el borrado si el rol tiene usuarios activos y lo explica en el aviso.
    const ok = await escribir(
      () => deleteRolByIdAsync(token, fila.id),
      "No se pudo borrar el rol"
    );
    if (!ok) return;
    setLocalReload((actual) => !actual);
    aviso.exito(`Rol ${fila.rolName} borrado`);
  };

  const pedirBorrar = (fila) =>
    pedirConfirmacion({
      titulo: "Borrar rol",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se borrará el rol ${fila.rolName} y se apagarán todos sus permisos.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Si todavía tiene usuarios activos no se podrá borrar: primero cámbielos de rol.
          </Typography>
        </>
      ),
      textoConfirmar: "Borrar rol",
      peligro: true,
      onConfirmar: () => borrar(fila),
    });

  const puedeAgregar = tienePermiso(access, "SECURITY CREATE");
  const puedeEditar = tienePermiso(access, "SECURITY UPDATE");
  const puedeBorrar = tienePermiso(access, "SECURITY DELETE");

  const botonAgregar = (
    <Button onClick={() => navigate(`${ruta}/addrol`)} startIcon={<AddIcon />} variant="contained">
      Agregar rol
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={5} columnas={3} etiqueta="Cargando roles…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los roles"
        }
        onReintentar={() => setLocalReload((actual) => !actual)}
      />
    );
  } else if (withSearch.length === 0) {
    contenido = termino ? (
      <EstadoVacio
        titulo="Sin resultados"
        descripcion={`Ningún rol coincide con «${searchTerm.trim()}».`}
        accion={
          <Button variant="outlined" onClick={() => onChangeSearch("")}>
            Borrar búsqueda
          </Button>
        }
      />
    ) : (
      <EstadoVacio
        titulo="Todavía no hay roles"
        descripcion="Un rol agrupa los permisos de un puesto: qué pueden ver y hacer sus usuarios."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Roles" sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}>
                  #
                </TableCell>
                <TableCell sx={encabezadoTabla}>Rol</TableCell>
                <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}>
                  Rol superior
                </TableCell>
                <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => {
                const superior =
                  row.idRolSuperior === row.id ? "— (rol raíz)" : nombreDe(row.idRolSuperior) ?? "—";
                return (
                  <TableRow key={row.id} hover>
                    <TableCell
                      sx={{ fontVariantNumeric: "tabular-nums", display: { xs: "none", sm: "table-cell" } }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.rolName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: { xs: "block", sm: "none" } }}
                      >
                        {`Superior: ${superior}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>{superior}</TableCell>
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Tooltip title={puedeEditar ? "Editar rol y permisos" : "Ver permisos"}>
                        <IconButton
                          aria-label={`${puedeEditar ? "Editar" : "Ver"} el rol ${row.rolName}`}
                          onClick={() => navigate(`${ruta}/rol/${row.id}`)}
                          sx={{ color: "primary.main" }}
                        >
                          {puedeEditar ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                      {puedeBorrar ? (
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones del rol ${row.rolName}`}
                            aria-haspopup="menu"
                            onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={PAGINACION_ARIA}
        />
      </>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Roles"
        subtitulo="Qué puede ver y hacer cada puesto en el sistema"
        icono={<FontAwesomeIcon icon={faUserShield} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="Nombre del rol"
          inputProps={{ "aria-label": "Buscar rol", autoComplete: "off" }}
          sx={{ width: { xs: "100%", md: 380 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => onChangeSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {contenido}
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirBorrar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Borrar rol</ListItemText>
        </MenuItem>
      </Menu>

      {confirmacion}
    </Container>
  );
};

export default RolList;
